import React from "react";
import {
  Box,
  Checkbox,
  Stack,
  Typography,
} from "@mui/material";

export type DataCardField = {
  label: string;
  value: React.ReactNode;
};

export function DataCardShell({
  title,
  subtitle,
  chips,
  fields,
  primaryAction,
  secondaryActions,
  selected,
  onSelectChange,
  selectable,
  highlight,
  borderColor,
  bgcolor,
  footer,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  chips?: React.ReactNode;
  fields?: DataCardField[];
  primaryAction?: React.ReactNode;
  secondaryActions?: React.ReactNode;
  selectable?: boolean;
  selected?: boolean;
  onSelectChange?: (checked: boolean) => void;
  highlight?: boolean;
  borderColor?: string;
  bgcolor?: string;
  footer?: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        border: "1px solid",
        borderColor: borderColor || (highlight ? "#86efac" : "#d1d5db"),
        bgcolor: bgcolor || (highlight ? "rgb(196 240 196)" : "white"),
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
        {selectable && (
          <Checkbox
            size="small"
            checked={Boolean(selected)}
            onChange={(e) => onSelectChange?.(e.target.checked)}
            sx={{ p: 0.5, mt: -0.25, flexShrink: 0 }}
            inputProps={{ "aria-label": "Select row" }}
          />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="flex-start"
            justifyContent="space-between"
            flexWrap="wrap"
            useFlexGap
            sx={{ minWidth: 0 }}
          >
            <Box sx={{ minWidth: 0, flex: "1 1 160px" }}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: "0.98rem", sm: "1.05rem" },
                  fontWeight: 800,
                  lineHeight: 1.3,
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {title}
              </Typography>
              {subtitle ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.25, overflowWrap: "anywhere", wordBreak: "break-word" }}
                >
                  {subtitle}
                </Typography>
              ) : null}
            </Box>
            {chips ? (
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {chips}
              </Stack>
            ) : null}
          </Stack>
        </Box>
      </Stack>

      {fields && fields.length > 0 ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns:
              fields.length === 1
                ? "1fr"
                : { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 1.25,
            minWidth: 0,
          }}
        >
          {fields.map((field) => (
            <Box key={field.label} sx={{ minWidth: 0, width: "100%" }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: "#6b7280", display: "block", mb: 0.25 }}
              >
                {field.label}
              </Typography>
              <Box
                sx={{
                  fontSize: "0.875rem",
                  color: "#111827",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {field.value ?? "—"}
              </Box>
            </Box>
          ))}
        </Box>
      ) : null}

      {footer}

      {(primaryAction || secondaryActions) && (
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: "auto", pt: 0.5, minWidth: 0 }}
          alignItems="center"
        >
          {primaryAction}
          {secondaryActions}
        </Stack>
      )}
    </Box>
  );
}
